import {
  MicrophoneIcon,
  useCameraStore,
  useTheme,
  useUserData,
  VideoIcon,
} from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import { RemoteParticipant, Track } from 'livekit-client';
import React, { useMemo, useState } from 'react';
import ScreenShareVideo from './ScreenShareVideo';

interface ScreenShare {
  id: string;
  participantIdentity: string;
  trackSid: string;
  publication: any;
}

interface FlikRoomProps {
  screenShares: ScreenShare[];
  session?: any;
  // Cursor sharing props
  room?: any; // LiveKit room instance
  isCursorSharingEnabled?: boolean;
}

// Participant video component - moved outside to prevent hooks issues
const ParticipantVideo = React.memo(
  ({
    participant,
    className = '',
    session,
    provideTailwind,
    getInitials,
    isLocal = false,
    cameraStream,
    onToggleMic,
    onToggleVideo,
    isMicOn,
    isVideoOn,
  }: {
    participant: RemoteParticipant;
    className?: string;
    session?: any;
    provideTailwind: (className: string) => string;
    getInitials: (identity: string) => string;
    isLocal?: boolean;
    cameraStream?: MediaStream;
    onToggleMic?: () => void;
    onToggleVideo?: () => void;
    isMicOn?: boolean;
    isVideoOn?: boolean;
  }) => {
    const hasCameraVideo = isLocal
      ? isVideoOn && cameraStream
      : Array.from(participant.trackPublications.values()).some(
          (pub: any) =>
            pub.source === Track.Source.Camera &&
            pub.kind === Track.Kind.Video &&
            pub.isSubscribed &&
            !pub.isMuted &&
            pub.track,
        );

    const videoRefCb = React.useCallback(
      (el: HTMLVideoElement | null) => {
        if (el) {
          session?.videoRefs?.current?.set(participant.identity, el);
          // Find the camera track for this participant
          const cameraTrack = Array.from(
            participant.trackPublications.values(),
          ).find(
            (pub: any) =>
              pub.source === Track.Source.Camera &&
              pub.kind === Track.Kind.Video &&
              pub.isSubscribed &&
              !pub.isMuted &&
              pub.track,
          );

          session?.registerVideoElement?.(
            participant.identity,
            el,
            false,
            (cameraTrack as any)?.trackSid,
          );
        } else {
          session?.videoRefs?.current?.delete(participant.identity);
          session?.registerVideoElement?.(participant.identity, null, false);
        }
      },
      [participant.identity, participant.trackPublications, session],
    );

    return (
      <div
        className={`relative ${className} rounded-2xl overflow-hidden flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${provideTailwind(
          'border-primary',
        )} border-opacity-20`}
        style={{
          // Let the video maintain natural aspect ratio
          width: '100%',
          aspectRatio: '16/9',
        }}
      >
        {hasCameraVideo ? (
          <video
            ref={
              isLocal
                ? (el) => {
                    if (el && cameraStream) {
                      el.srcObject = cameraStream;
                    } else if (el) {
                      el.srcObject = null;
                    }
                  }
                : videoRefCb
            }
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            {/* Circular avatar similar to NavBar */}
            <div
              className={`w-20 h-20 rounded-full flex items-center justify-center ${provideTailwind(
                'bg-secondary',
              )} border-2 ${provideTailwind('border-white')} shadow-lg`}
            >
              {(() => {
                const initials = getInitials(participant.identity);
                if (initials === 'LOADING') {
                  return (
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  );
                }
                return (
                  <span
                    className={`${provideTailwind(
                      'text-primary',
                    )} font-semibold text-2xl`}
                  >
                    {initials}
                  </span>
                );
              })()}
            </div>
          </div>
        )}

        {/* Control buttons for local participant */}
        {isLocal && (
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex flex-col space-y-1">
            {/* Microphone Toggle */}
            <button
              onClick={onToggleMic ?? (() => {})}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                isMicOn
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-red-500 hover:bg-red-600'
              } ${provideTailwind('text-white')} shadow-lg hover:shadow-xl`}
            >
              <MicrophoneIcon
                width={16}
                height={16}
                className={provideTailwind('text-white')}
              />
            </button>

            {/* Camera Toggle */}
            <button
              onClick={onToggleVideo ?? (() => {})}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                isVideoOn
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-red-500 hover:bg-red-600'
              } ${provideTailwind('text-white')} shadow-lg hover:shadow-xl`}
            >
              <VideoIcon
                width={16}
                height={16}
                className={provideTailwind('text-white')}
              />
            </button>
          </div>
        )}

        {/* Subtle border glow */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-blue-500/20 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent re-renders when only other participants change
    return (
      prevProps.participant.identity === nextProps.participant.identity &&
      prevProps.participant.trackPublications ===
        nextProps.participant.trackPublications &&
      prevProps.className === nextProps.className &&
      prevProps.session === nextProps.session &&
      prevProps.provideTailwind === nextProps.provideTailwind &&
      prevProps.getInitials === nextProps.getInitials
    );
  },
);

const FlikRoom: React.FC<FlikRoomProps> = ({
  screenShares,
  session,
  room,
  isCursorSharingEnabled = false,
}) => {
  const { provideTailwind } = useTheme();
  const apiClient = useReactQuery();
  const { cameraStream } = useCameraStore();
  const { userData, initials } = useUserData();

  // Use only remote participants (exclude local participant from main grid)
  const participants = React.useMemo(() => {
    const allParticipants = session?.visibleParticipants ?? [];
    // Remove the first participant (local participant) from the grid
    return allParticipants.slice(1);
  }, [session?.visibleParticipants]);

  // Get unique participant identities for whois API call
  const participantIdentities = useMemo(() => {
    const regularParticipantIds = participants.map(
      (p: RemoteParticipant) => p.identity,
    );
    const screenShareParticipantIds = screenShares.map(
      (share: ScreenShare) => share.participantIdentity,
    );

    console.log('Regular participant IDs:', regularParticipantIds);
    console.log('Screen share participant IDs:', screenShareParticipantIds);
    console.log('Screen shares array:', screenShares);

    const allIds = Array.from(
      new Set([...regularParticipantIds, ...screenShareParticipantIds]),
    );

    console.log('All participant IDs being sent to API:', allIds);
    return allIds;
  }, [participants, screenShares]);

  // Use enhanced user data caching system
  const { getMultipleUsersData, getUserDisplayName, getUserInitials } =
    useUserData();
  const [participantInfoMap, setParticipantInfoMap] = useState<
    Map<string, any>
  >(new Map());

  // Track previous identities to avoid unnecessary API calls
  const prevIdentitiesRef = React.useRef<string[]>([]);

  // Fetch participant info when participants change using enhanced caching
  React.useEffect(() => {
    const currentIdentities = participantIdentities.sort();
    const prevIdentities = prevIdentitiesRef.current.sort();

    // Only fetch if identities actually changed
    if (
      currentIdentities.length > 0 &&
      (currentIdentities.length !== prevIdentities.length ||
        !currentIdentities.every((id, index) => id === prevIdentities[index]))
    ) {
      console.log('Fetching user data for identities:', participantIdentities);
      prevIdentitiesRef.current = [...(participantIdentities as string[])];

      // Use the enhanced caching system
      getMultipleUsersData(participantIdentities as string[])
        .then((results) => {
          const newMap = new Map();
          Object.entries(results).forEach(([identity, userData]) => {
            if (userData) {
              newMap.set(identity, userData);
            }
          });
          setParticipantInfoMap(newMap);
        })
        .catch((error) => {
          console.error('Failed to fetch participant info:', error);
        });
    }
  }, [participantIdentities, getMultipleUsersData]);

  // Helper function to get initials from user info
  const getInitials = (
    identity: string,
    isLocal: boolean = false,
  ): string | 'LOADING' => {
    // Handle local participant using userData
    if (isLocal && userData) {
      return initials;
    }

    // First check the local participantInfoMap
    const userInfo = participantInfoMap.get(identity);
    if (userInfo?.firstName && userInfo?.lastName) {
      return `${userInfo.firstName.charAt(0)}${userInfo.lastName.charAt(
        0,
      )}`.toUpperCase();
    } else if (userInfo?.firstName) {
      return userInfo.firstName.charAt(0).toUpperCase();
    } else if (userInfo?.lastName) {
      return userInfo.lastName.charAt(0).toUpperCase();
    }

    // Fallback to enhanced caching system
    return getUserInitials(identity);
  };

  // Handle screen shares - limit to maximum 4 for layout purposes
  const limitedScreenShares = screenShares.slice(0, 4);
  const hasScreenShares = limitedScreenShares.length > 0;
  const screenShareCount = limitedScreenShares.length;

  // Remove mouse event handlers to prevent flashing
  // Header is now always visible

  return (
    <div
      className={`fixed inset-0 ${provideTailwind('bg-primary')}`}
      style={{
        width: '100vw',
        height: 'calc(100vh - 5rem)', // Adjust height to account for NavBar + gap
        overflow: 'hidden',
        top: 'calc(4rem + 1rem)', // Start below NavBar + small gap (4rem + 1rem = 5rem/80px)
      }}
    >
      {/* Left Control Panel */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-80 ${provideTailwind(
          'bg-primary',
        )} bg-opacity-95 backdrop-blur-md z-20`}
      >
        <div className="flex flex-col h-full p-6">
          {/* Session Name */}
          <div className="mb-8">
            <h2
              className={`text-lg font-semibold ${provideTailwind(
                'text-primary',
              )} mb-2`}
            >
              {session?.title || 'Untitled Session'}
            </h2>
          </div>

          {/* User Controls */}
          <div className="flex-1">
            {/* All Participants List - show when there are screen shares */}
            {hasScreenShares && (
              <div className="mb-6">
                <h3
                  className={`text-sm font-medium ${provideTailwind(
                    'text-primary',
                  )} mb-3`}
                >
                  Participants
                </h3>
                <div className="space-y-3">
                  {/* Local User - using ParticipantVideo component for consistency */}
                  <ParticipantVideo
                    participant={
                      {
                        identity: 'local',
                        trackPublications: new Map(),
                      } as RemoteParticipant
                    }
                    session={session}
                    provideTailwind={provideTailwind}
                    getInitials={(identity) =>
                      identity === 'local'
                        ? userData
                          ? getInitials('local', true)
                          : 'You'
                        : getInitials(identity)
                    }
                    className={`${provideTailwind(
                      'bg-secondary',
                    )} rounded-2xl border-2 ${provideTailwind(
                      'border-primary',
                    )} border-opacity-20`}
                    isLocal={true}
                    cameraStream={cameraStream || undefined}
                    onToggleMic={session?.onToggleMic}
                    onToggleVideo={session?.onToggleVideo}
                    isMicOn={session?.isMicOn}
                    isVideoOn={session?.isVideoOn}
                  />

                  {/* Remote Participants */}
                  {participants.map((participant: RemoteParticipant) => (
                    <ParticipantVideo
                      key={participant.identity}
                      participant={participant}
                      session={session}
                      provideTailwind={provideTailwind}
                      getInitials={getInitials}
                      className={`${provideTailwind(
                        'bg-secondary',
                      )} rounded-2xl border-2 ${provideTailwind(
                        'border-primary',
                      )} border-opacity-20`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Local User Video Preview - only show when no screen shares */}
            {!hasScreenShares && (
              <div className="mb-6">
                <div
                  className={`relative group ${provideTailwind(
                    'bg-secondary',
                  )} rounded-xl overflow-hidden aspect-video flex items-center justify-center shadow-lg border-2 ${provideTailwind(
                    'border-primary',
                  )} border-opacity-20`}
                >
                  {session?.localVideoEnabled && cameraStream ? (
                    <video
                      ref={(el) => {
                        if (el && cameraStream) {
                          el.srcObject = cameraStream;
                        } else if (el) {
                          el.srcObject = null;
                        }
                      }}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center">
                      {/* Circular avatar for local user */}
                      <div
                        className={`w-16 h-16 rounded-full flex items-center justify-center ${provideTailwind(
                          'bg-secondary',
                        )} border-2 ${provideTailwind(
                          'border-white',
                        )} shadow-lg`}
                      >
                        <span
                          className={`${provideTailwind(
                            'text-primary',
                          )} font-semibold text-lg`}
                        >
                          {userData ? getInitials('local', true) : 'You'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Overlay Controls - Always visible */}
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex flex-col space-y-2">
                    {/* Microphone Toggle */}
                    <button
                      onClick={session?.onToggleMic ?? (() => {})}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        session?.isMicOn
                          ? 'bg-green-500 hover:bg-green-600'
                          : 'bg-red-500 hover:bg-red-600'
                      } ${provideTailwind(
                        'text-white',
                      )} shadow-lg hover:shadow-xl`}
                    >
                      <MicrophoneIcon
                        width={20}
                        height={20}
                        className="${provideTailwind('text-white')}"
                      />
                    </button>

                    {/* Camera Toggle */}
                    <button
                      onClick={session?.onToggleVideo ?? (() => {})}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        session?.isVideoOn
                          ? 'bg-green-500 hover:bg-green-600'
                          : 'bg-red-500 hover:bg-red-600'
                      } ${provideTailwind(
                        'text-white',
                      )} shadow-lg hover:shadow-xl`}
                    >
                      <VideoIcon
                        width={20}
                        height={20}
                        className="${provideTailwind('text-white')}"
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Leave Button */}
          <div className="mt-auto">
            <button
              onClick={session?.onLeaveRoom ?? (() => {})}
              className={`w-full py-3 px-4 ${provideTailwind(
                'bg-red-button',
              )} ${provideTailwind('hover:bg-red-button')} ${provideTailwind(
                'text-red-button',
              )} rounded-xl transition-all duration-300 shadow-md hover:shadow-lg font-medium flex items-center justify-center space-x-2`}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                className="${provideTailwind('text-white')}"
              >
                <path
                  d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline
                  points="16,17 21,12 16,7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <line
                  x1="21"
                  y1="12"
                  x2="9"
                  y2="12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Leave Flik</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="pl-80 h-full">
        {!hasScreenShares ? (
          // Dynamic grid layout for participants when no screen shares
          <div className="h-full p-4">
            {(() => {
              const participantCount = participants.length;
              let gridClasses = '';
              let containerClasses = '';

              if (participantCount === 1) {
                // Single participant takes full space
                gridClasses = 'grid-cols-1';
                containerClasses = 'h-full flex items-center justify-center';
              } else if (participantCount === 2) {
                // Two participants side by side
                gridClasses = 'grid-cols-2';
                containerClasses = 'h-full';
              } else if (participantCount === 3) {
                // Three participants: 2 on top, 1 on bottom
                gridClasses = 'grid-cols-2 grid-rows-2';
                containerClasses = 'h-full';
              } else if (participantCount === 4) {
                // Four participants: 2x2 grid
                gridClasses = 'grid-cols-2 grid-rows-2';
                containerClasses = 'h-full';
              } else {
                // 5+ participants: responsive grid
                gridClasses =
                  'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';
                containerClasses = 'h-full';
              }

              return (
                <div
                  className={`grid ${gridClasses} gap-4 ${containerClasses}`}
                >
                  {participants.map(
                    (participant: RemoteParticipant, index: number) => {
                      // For 3 participants, make the third one span both columns
                      const isThirdParticipant =
                        participantCount === 3 && index === 2;
                      const spanClasses = isThirdParticipant
                        ? 'col-span-2'
                        : '';

                      // Special styling for single participant
                      const isSingleParticipant = participantCount === 1;
                      const singleParticipantClasses = isSingleParticipant
                        ? 'w-full h-full max-w-4xl max-h-4xl'
                        : '';

                      return (
                        <ParticipantVideo
                          key={participant.identity}
                          participant={participant}
                          session={session}
                          provideTailwind={provideTailwind}
                          getInitials={getInitials}
                          className={`${provideTailwind(
                            'bg-secondary',
                          )} rounded-2xl border-2 ${provideTailwind(
                            'border-primary',
                          )} border-opacity-20 ${spanClasses} ${singleParticipantClasses}`}
                        />
                      );
                    },
                  )}
                </div>
              );
            })()}
          </div>
        ) : screenShareCount === 1 ? (
          // Single screen share - full screen
          <div className="h-full">
            <ScreenShareVideo
              share={limitedScreenShares[0]}
              className="w-full h-full"
              session={session}
              provideTailwind={provideTailwind}
              getInitials={getInitials}
              participantInfoMap={participantInfoMap}
              room={room}
              isCursorSharingEnabled={isCursorSharingEnabled}
            />
          </div>
        ) : screenShareCount === 2 ? (
          // Two screen shares - side by side
          <div className="h-full flex">
            <div className="flex-1 relative">
              <ScreenShareVideo
                share={limitedScreenShares[0]}
                className="w-full h-full"
                session={session}
                provideTailwind={provideTailwind}
                getInitials={getInitials}
                participantInfoMap={participantInfoMap}
                room={room}
                isCursorSharingEnabled={isCursorSharingEnabled}
              />
            </div>
            <div className="flex-1 relative">
              <ScreenShareVideo
                share={limitedScreenShares[1]}
                className="w-full h-full"
                session={session}
                provideTailwind={provideTailwind}
                getInitials={getInitials}
                participantInfoMap={participantInfoMap}
                room={room}
                isCursorSharingEnabled={isCursorSharingEnabled}
              />
            </div>
          </div>
        ) : screenShareCount === 3 ? (
          // Three screen shares - 2 on top, 1 on bottom
          <div className="h-full flex flex-col">
            <div className="flex-1 flex">
              <div className="flex-1 relative">
                <ScreenShareVideo
                  share={limitedScreenShares[0]}
                  className="w-full h-full"
                  session={session}
                  provideTailwind={provideTailwind}
                  getInitials={getInitials}
                  participantInfoMap={participantInfoMap}
                  room={room}
                  isCursorSharingEnabled={isCursorSharingEnabled}
                />
              </div>
              <div className="flex-1 relative">
                <ScreenShareVideo
                  share={limitedScreenShares[1]}
                  className="w-full h-full"
                  session={session}
                  provideTailwind={provideTailwind}
                  getInitials={getInitials}
                  participantInfoMap={participantInfoMap}
                  room={room}
                  isCursorSharingEnabled={isCursorSharingEnabled}
                />
              </div>
            </div>
            <div className="flex-1 relative">
              <ScreenShareVideo
                share={limitedScreenShares[2]}
                className="w-full h-full"
                session={session}
                provideTailwind={provideTailwind}
                getInitials={getInitials}
                participantInfoMap={participantInfoMap}
                room={room}
                isCursorSharingEnabled={isCursorSharingEnabled}
              />
            </div>
          </div>
        ) : (
          // Four or more screen shares - 2x2 grid
          <div className="h-full grid grid-cols-2 grid-rows-2">
            {limitedScreenShares.slice(0, 4).map((share) => (
              <div key={share.id} className="relative">
                <ScreenShareVideo
                  share={share}
                  className="w-full h-full"
                  session={session}
                  provideTailwind={provideTailwind}
                  getInitials={getInitials}
                  participantInfoMap={participantInfoMap}
                  room={room}
                  isCursorSharingEnabled={isCursorSharingEnabled}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlikRoom;
